import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Building2, Plus, Settings } from 'lucide-react';
import { supabasePermissionsService } from '../../services/supabase-permissions.service';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTheme } from '../../hooks/useAppTheme';
export function OrganizationSwitcher({ currentOrgId, onOrganizationChange, }) {
    const navigate = useNavigate();
    const { refreshUserProfile } = useAuth();
    const { isDark } = useAppTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    useEffect(() => {
        loadOrganizations();
    }, []);
    // Set up real-time subscriptions to organization changes
    useEffect(() => {
        const currentUser = supabasePermissionsService.getCurrentUserProfile().then(user => user?.id);
        if (!currentUser)
            return;
        currentUser.then((userId) => {
            if (!userId)
                return;
            // Subscribe to organization_members changes for the current user
            const orgMembersSubscription = supabase
                .channel(`org_members_${userId}`)
                .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'organization_members',
                filter: `user_id=eq.${userId}`,
            }, () => {
                console.log('📡 Real-time: organization_members changed, reloading...');
                loadOrganizations();
            })
                .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('✅ Subscribed to organization_members changes');
                }
            });
            // Subscribe to organizations changes
            const orgsSubscription = supabase
                .channel(`organizations_all`)
                .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'organizations',
            }, () => {
                console.log('📡 Real-time: organizations changed, reloading...');
                loadOrganizations();
            })
                .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('✅ Subscribed to organizations changes');
                }
            });
            return () => {
                console.log('🔌 Unsubscribing from organization real-time channels');
                supabase.removeChannel(orgMembersSubscription);
                supabase.removeChannel(orgsSubscription);
            };
        });
    }, []);
    // Calculate dropdown position
    const calculateDropdownPosition = (triggerElement) => {
        const rect = triggerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownWidth = rect.width; // Match trigger width (left-0 right-0)
        const dropdownMaxHeight = 384; // max-h-96
        let top = rect.bottom + 8; // Default: below trigger with 8px gap (mt-2)
        let left = rect.left; // Default: align left edge
        // Adjust if dropdown would go off bottom of screen
        if (top + dropdownMaxHeight > viewportHeight) {
            top = Math.max(8, viewportHeight - dropdownMaxHeight - 8);
        }
        // Adjust if dropdown would go off right of screen
        if (left + dropdownWidth > viewportWidth - 8) {
            left = viewportWidth - dropdownWidth - 8;
        }
        // Adjust if dropdown would go off left of screen
        if (left < 8) {
            left = 8;
        }
        return { top, left, width: rect.width };
    };
    useEffect(() => {
        // Close dropdown when clicking outside
        function handleClickOutside(event) {
            const target = event.target;
            const clickedOutside = dropdownRef.current && !dropdownRef.current.contains(target) &&
                triggerRef.current && !triggerRef.current.contains(target);
            if (clickedOutside) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const currentUser = await supabasePermissionsService.getCurrentUserProfile();
            if (!currentUser) {
                console.warn('No user found');
                return;
            }
            const userOrgs = await supabasePermissionsService.getUserOrganizations(currentUser.id);
            setOrganizations(userOrgs);
            // Set current organization
            if (currentOrgId) {
                const current = userOrgs.find((org) => org.id === currentOrgId);
                setCurrentOrg(current || null);
            }
            else if (currentUser.organization_id) {
                const current = userOrgs.find((org) => org.id === currentUser.organization_id);
                setCurrentOrg(current || null);
            }
        }
        catch (error) {
            console.error('Error loading organizations:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleOrganizationSelect = async (org) => {
        setCurrentOrg(org);
        setIsOpen(false);
        // Update user's current organization in profile
        try {
            const currentUser = await supabasePermissionsService.getCurrentUserProfile();
            if (currentUser) {
                await supabasePermissionsService.updateUserProfile(currentUser.id, {
                    organization_id: org.id,
                });
                // Refresh the user profile in AuthContext to trigger workspace reload
                await refreshUserProfile();
            }
        }
        catch (error) {
            console.error('Error updating current organization:', error);
        }
        // Notify parent component
        if (onOrganizationChange) {
            onOrganizationChange(org.id);
        }
    };
    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'owner':
                return 'bg-purple-100 text-purple-700';
            case 'admin':
                return 'bg-blue-100 text-blue-700';
            case 'member':
                return 'bg-green-100 text-green-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };
    if (loading) {
        return (_jsxs("div", { className: "flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 animate-pulse", children: [_jsx("div", { className: "w-8 h-8 bg-gray-300 rounded-lg" }), _jsx("div", { className: "w-24 h-4 bg-gray-300 rounded" })] }));
    }
    // Always show the switcher, even if no organization is set
    if (organizations.length === 0) {
        return (_jsxs("div", { className: "flex items-center space-x-3 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200", children: [_jsx("div", { className: "w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center", children: _jsx(Building2, { className: "w-5 h-5 text-gray-500" }) }), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: "text-sm font-medium text-gray-600", children: "No Organization" }), _jsx("div", { className: "text-xs text-gray-500", children: "Create one to get started" })] })] }));
    }
    if (!currentOrg) {
        // Fallback to first org if none is selected
        setCurrentOrg(organizations[0]);
        return null; // Will re-render with currentOrg set
    }
    return (_jsxs("div", { className: "relative", children: [_jsxs("button", { ref: triggerRef, onClick: () => {
                    if (!isOpen && triggerRef.current) {
                        const position = calculateDropdownPosition(triggerRef.current);
                        setDropdownPosition(position);
                    }
                    setIsOpen(!isOpen);
                }, className: `flex items-center space-x-3 w-full px-3 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-cyan-bright/10' : 'hover:bg-cyan-deep/5'}`, children: [currentOrg.avatar_url ? (_jsx("img", { src: currentOrg.avatar_url, alt: currentOrg.name, className: "w-8 h-8 rounded-lg object-cover" })) : (_jsx("div", { className: "w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center", children: _jsx("span", { className: "text-white text-sm font-semibold", children: currentOrg.name.charAt(0).toUpperCase() }) })), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: `text-sm font-medium truncate max-w-[150px] ${isDark ? 'text-white' : 'text-gray-900'}`, children: currentOrg.name }), _jsx("div", { className: `text-xs font-medium px-1.5 py-0.5 rounded inline-block ${getRoleBadgeColor(currentOrg.memberRole)}`, children: currentOrg.memberRole })] }), _jsx(ChevronDown, { className: `w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}` })] }), isOpen && createPortal(_jsxs("div", { ref: dropdownRef, className: `fixed rounded-lg shadow-lg border z-[99998] py-2 max-h-96 overflow-y-auto ${isDark
                    ? 'bg-navy-deep border-cyan-bright/20'
                    : 'bg-white border-gray-200'}`, style: {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width || 280}px`
                }, children: [_jsxs("div", { className: "px-2", children: [_jsx("div", { className: `text-xs font-semibold uppercase tracking-wider px-3 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`, children: "Your Organizations" }), organizations.map((org) => (_jsxs("button", { onClick: () => handleOrganizationSelect(org), className: `w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-cyan-bright/10' : 'hover:bg-cyan-deep/5'}`, children: [org.avatar_url ? (_jsx("img", { src: org.avatar_url, alt: org.name, className: "w-8 h-8 rounded-lg object-cover" })) : (_jsx("div", { className: "w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center", children: _jsx("span", { className: "text-white text-sm font-semibold", children: org.name.charAt(0).toUpperCase() }) })), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: `text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`, children: org.name }), _jsx("div", { className: `text-xs font-medium px-1.5 py-0.5 rounded inline-block ${getRoleBadgeColor(org.memberRole)}`, children: org.memberRole })] }), currentOrg?.id === org.id && (_jsx(Check, { className: `w-5 h-5 flex-shrink-0 ${isDark ? 'text-cyan-bright' : 'text-blue-600'}` }))] }, org.id)))] }), _jsx("div", { className: `border-t my-2 ${isDark ? 'border-cyan-bright/10' : 'border-gray-200'}` }), _jsx("div", { className: "px-2", children: _jsxs("button", { onClick: () => {
                                setIsOpen(false);
                                navigate('/app/organization/settings');
                            }, className: `w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left ${isDark
                                ? 'text-cyan-bright hover:bg-cyan-bright/10 hover:text-white'
                                : 'text-cyan-deep hover:bg-cyan-deep/5 hover:text-gray-900'}`, children: [_jsx("div", { className: `w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-cyan-bright/10' : 'bg-gray-200'}`, children: _jsx(Settings, { className: `w-5 h-5 ${isDark ? 'text-cyan-bright' : 'text-gray-600'}` }) }), _jsx("span", { className: "text-sm font-medium", children: "Organization Settings" })] }) }), _jsx("div", { className: `border-t my-2 ${isDark ? 'border-cyan-bright/10' : 'border-gray-200'}` }), _jsx("div", { className: "px-2", children: _jsxs("button", { onClick: () => {
                                // TODO: Implement create organization modal
                                console.log('Create new organization clicked');
                            }, className: `w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-left ${isDark
                                ? 'text-cyan-bright hover:bg-cyan-bright/10 hover:text-white'
                                : 'text-cyan-deep hover:bg-cyan-deep/5 hover:text-gray-900'}`, children: [_jsx("div", { className: `w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-cyan-bright/10' : 'bg-gray-200'}`, children: _jsx(Plus, { className: `w-5 h-5 ${isDark ? 'text-cyan-bright' : 'text-gray-600'}` }) }), _jsx("span", { className: "text-sm font-medium", children: "Create Organization" })] }) })] }), document.body)] }));
}
